import { useState } from 'react';
import { hotTargets, indexTargets, type TargetItem } from '@/mocks/heiLiao';

function trendIcon(t: TargetItem['trend']) {
  if (t === 'up') return <i className="ri-arrow-up-line text-[11px] text-primary-500"></i>;
  if (t === 'down') return <i className="ri-arrow-down-line text-[11px] text-accent-600"></i>;
  return <i className="ri-arrow-right-line text-[11px] text-foreground-400"></i>;
}

function RankCard({ item, compact }: { item: TargetItem; compact?: boolean }) {
  const [follow, setFollow] = useState(false);
  const rankHigh = item.rank <= 3;
  return (
    <div
      className={`flex gap-3.5 rounded-xl border border-background-200/70 p-4 transition-all hover:-translate-y-0.5 hover:border-primary-200 ${
        rankHigh ? 'bg-gradient-to-br from-background-50 to-primary-50/50' : 'bg-background-50'
      }`}
    >
      {/* 排名块 */}
      <div className="flex w-9 shrink-0 flex-col items-center gap-1">
        <span
          className={`flex h-9 w-9 items-center justify-center rounded-lg font-heading text-lg ${
            rankHigh ? 'bg-primary-500 text-background-50' : 'bg-secondary-200/70 text-secondary-900'
          }`}
        >
          {item.rank}
        </span>
        <span className="flex h-4 items-center gap-0.5 text-[12px] font-bold text-accent-600">{item.heat}{trendIcon(item.trend)}</span>
      </div>

      <div className="min-w-0 flex-1">
        <div className="mb-1 flex flex-wrap items-center gap-1.5">
          <h3 className="font-heading text-[14px] font-bold text-foreground-950">{item.name}</h3>
          <span className="rounded bg-secondary-100 px-1.5 py-0.5 text-[10px] font-medium text-secondary-900">{item.tag}</span>
        </div>
        <a href="#expose-index" className="block cursor-pointer">
          <p className="text-[13px] font-semibold leading-snug text-foreground-800 hover:text-primary-600">{item.title}</p>
        </a>
        {!compact && (
          <p className="mt-1 text-[12px] leading-relaxed text-foreground-500">{item.desc}</p>
        )}
        <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[11px] text-foreground-400">
          {item.tags.map((tg) => (
            <span key={tg} className="rounded bg-background-200/60 px-1.5 py-0.5 text-foreground-500">{tg}</span>
          ))}
        </div>
        <div className="mt-2.5 flex items-center justify-between border-t border-background-200/60 pt-2">
          <span className="text-[11px] text-foreground-400">
            <i className="ri-refresh-line mr-1 text-[11px]"></i>{item.update}
          </span>
          <button
            onClick={() => setFollow((v) => !v)}
            className={`flex items-center gap-1 whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] transition-colors ${
              follow
                ? 'border-primary-300 bg-primary-500 text-background-50'
                : 'border-background-300/70 text-foreground-600 hover:border-primary-300 hover:text-primary-600'
            }`}
          >
            <i className={follow ? 'ri-star-fill text-[11px]' : 'ri-star-line text-[11px]'}></i>
            {follow ? '已追' : '追踪主角'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ExposeIndex() {
  return (
    <section id="expose-index" className="mx-auto w-full max-w-[1440px] scroll-mt-24 px-4 py-6 md:px-6">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* 大榜单 */}
        <div className="lg:col-span-2">
          <div className="mb-4 flex items-center justify-between border-b border-background-200/70 pb-3">
            <div className="flex items-center gap-2.5">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-500 text-background-50">
                <i className="ri-medal-line text-base"></i>
              </span>
              <div>
                <h2 className="font-heading text-lg leading-tight text-foreground-950">今日曝光主角榜单</h2>
                <p className="text-[11px] text-foreground-500">新鲜黑料主角按实时热度排序，热度每分钟刷新</p>
              </div>
            </div>
            <span className="hidden items-center gap-1.5 rounded-full border border-background-300/70 px-3 py-1 text-[11px] text-foreground-600 sm:flex">
              <i className="ri-equalizer-line text-[12px]"></i> 综合指数榜
            </span>
          </div>
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            {hotTargets.map((it) => (
              <RankCard key={it.id} item={it} />
            ))}
          </div>

          {/* 榜单延伸（更靠后的索引位） */}
          <div className="mt-4 rounded-xl border border-background-200/70 bg-background-100/40 p-3">
            <div className="mb-2 flex items-center gap-2 px-1 text-[12px] font-semibold text-foreground-600">
              <i className="ri-list-check-3 text-[13px] text-accent-600"></i> 11 – 20 位 · 热门候选区
            </div>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {indexTargets.slice(0, 4).map((it) => (
                <div key={it.id} className="flex items-center gap-2 rounded-lg bg-background-50 px-3 py-2">
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-[11px] font-bold text-secondary-900">
                    {it.rank}
                  </span>
                  <a href="#archive" className="truncate text-[12px] font-medium text-foreground-700 hover:text-primary-600">
                    {it.name}
                  </a>
                  <span className="ml-auto shrink-0 text-[11px] text-accent-600">{it.heat}{trendIcon(it.trend)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* 侧边：正在被全网追踪 */}
        <div className="flex flex-col gap-4">
          <div className="rounded-2xl border border-primary-200/80 bg-primary-50/60 p-4">
            <div className="mb-2 flex items-center gap-2">
              <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary-500 text-background-50">
                <i className="ri-radar-line text-sm"></i>
              </span>
              <h3 className="font-heading text-[14px] font-bold text-primary-900">全网追踪雷达</h3>
            </div>
            <p className="text-[12px] leading-relaxed text-primary-700/80">
              正在对今日曝光的主角做关键词全网巡更，抓到新扩散即推入新鲜黑料快讯流。
            </p>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {['旧档考古', '路透实拍', '商务单据', '行程表', '直播切片'].map((k) => (
                <span key={k} className="rounded-full bg-background-50 px-2 py-0.5 text-[11px] text-foreground-600">
                  {k}
                </span>
              ))}
            </div>
          </div>

          <div className="flex-1 rounded-2xl border border-background-200/70 bg-background-50 p-4">
            <h3 className="mb-3 flex items-center gap-2 text-[14px] font-bold text-foreground-950">
              <i className="ri-eye-line text-[15px] text-accent-600"></i> 更多人正在追踪
            </h3>
            <div className="space-y-2">
              {indexTargets.slice(4, 6).map((it) => (
                <div key={it.id} className="rounded-xl border border-background-200/60 bg-background-100/40 p-3">
                  <div className="mb-1 flex items-center gap-1.5">
                    <span className="font-heading text-[13px] font-bold text-foreground-900">{it.name}</span>
                    <span className="rounded bg-accent-50 px-1.5 py-0.5 text-[10px] text-accent-700">{it.tag}</span>
                  </div>
                  <a href="#archive" className="block cursor-pointer text-[12px] font-medium text-foreground-700 hover:text-primary-600">{it.title}</a>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}